import { useState, useRef, useEffect, useCallback } from 'react';
import { MapPin, Search, Loader2, Navigation, X } from 'lucide-react';
import { useUserLocation } from '../context/LocationContext';

/**
 * LocationInput — Global location autocomplete using OpenStreetMap Nominatim.
 * Supports street addresses, neighborhoods, cities, and countries worldwide.
 * Modular: can be upgraded to Google Places by swapping the search function.
 */
export default function LocationInput({ onSelect, onClose, compact = false }) {
  const { detectLocation } = useUserLocation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  // Debounced search via backend proxy (handles User-Agent, rate limiting)
  const searchLocation = useCallback((q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.length < 2) { setResults([]); return; }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
        const res = await fetch(
          `${API_BASE}/api/location/search?q=${encodeURIComponent(q)}&limit=6`
        );
        const data = await res.json();
        setResults(data.results || []);
        if (data.error) {
          // Show inline message instead of blocking
          setResults([{ id: 'error', neighborhood: data.error, city: '', region: '', country: '', lat: 0, lng: 0, isError: true }]);
        }
      } catch {
        setResults([]);
      }
      setLoading(false);
    }, 400);
  }, []);

  const handleInput = (e) => {
    const val = e.target.value;
    setQuery(val);
    searchLocation(val);
  };

  const handleSelect = (item) => {
    onSelect({
      neighborhood: item.neighborhood,
      city: item.city,
      region: item.region,
      country: item.country,
      lat: item.lat,
      lng: item.lng,
    });
  };

  const handleDetect = async () => {
    setDetecting(true);
    const loc = await detectLocation();
    setDetecting(false);
    if (loc) {
      onSelect(loc);
    }
  };

  return (
    <div className={`w-full ${compact ? '' : 'space-y-3'}`} data-testid="location-input">
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInput}
          placeholder="Enter your neighborhood, city, or address..."
          className="w-full pl-10 pr-10 py-3 rounded-xl bg-muted/50 border border-border text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-secondary/50 focus:border-secondary/50 transition-all"
          data-testid="location-search-input"
        />
        {query && (
          <button
            onClick={() => { setQuery(''); setResults([]); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-muted text-muted-foreground"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Auto-detect Button */}
      {!compact && (
        <button
          onClick={handleDetect}
          disabled={detecting}
          className="flex items-center gap-2 w-full px-4 py-2.5 rounded-lg bg-secondary/10 hover:bg-secondary/20 text-secondary text-sm font-medium transition-colors disabled:opacity-50"
          data-testid="location-detect-btn"
        >
          {detecting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Navigation className="w-4 h-4" />
          )}
          {detecting ? 'Detecting location...' : 'Use my current location'}
        </button>
      )}

      {/* Loading indicator */}
      {loading && (
        <div className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground">
          <Loader2 className="w-3 h-3 animate-spin" />
          Searching...
        </div>
      )}

      {/* Results */}
      {results.length > 0 && (
        <div className="max-h-64 overflow-y-auto rounded-xl border border-border bg-card shadow-lg divide-y divide-border" data-testid="location-results">
          {results.map((item) => (
            item.isError ? (
              <div key="error" className="px-4 py-3 text-xs text-muted-foreground text-center">
                {item.neighborhood}
              </div>
            ) : (
            <button
              key={item.id}
              onClick={() => handleSelect(item)}
              className="w-full flex items-start gap-3 px-4 py-3 hover:bg-muted/50 text-left transition-colors"
              data-testid={`location-result-${item.id}`}
            >
              <MapPin className="w-4 h-4 text-secondary mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground truncate">
                  {item.neighborhood || item.city}
                  {item.city && item.neighborhood && item.city !== item.neighborhood && (
                    <span className="text-muted-foreground">, {item.city}</span>
                  )}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {[item.region, item.country].filter(Boolean).join(', ')}
                </p>
              </div>
            </button>
            )
          ))}
        </div>
      )}

      {/* Empty state */}
      {query.length >= 2 && !loading && results.length === 0 && (
        <p className="text-xs text-muted-foreground text-center py-3">
          No results found. Try a different search.
        </p>
      )}
    </div>
  );
}
